import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../utils/formatters';

/**
 * CSV import tab state + handlers.
 *
 * Owns:
 *   • upload state (`importFile`, `importing`, `importResult`, `importForce`)
 *   • batches list + selected batch detail
 *   • link-investor dialog + edit-staging dialog
 *   • per-tab bulk-select (`selectedImport`) + sort state
 *
 * External deps:
 *   • `token` (for useCallback deps on fetchBatches)
 *   • `headers` (auth header — pinned once at the page level)
 *   • `fetchTransactions` — called after commit/approve so the main
 *     tab reflects the newly promoted rows.
 */
export function useCsvImport({ token, headers, fetchTransactions }) {
  // Upload
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importForce, setImportForce] = useState(false);

  // Batches
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [batchDetailLoading, setBatchDetailLoading] = useState(false);
  const [showCompletedBatches, setShowCompletedBatches] = useState(false);

  // Link-investor dialog
  const [linkDialog, setLinkDialog] = useState({ open: false, item: null });
  const [linkInvestorId, setLinkInvestorId] = useState('');

  // Edit-staging dialog
  const [editingStaging, setEditingStaging] = useState(null);
  const [editStagingType, setEditStagingType] = useState('');
  const [editStagingAmount, setEditStagingAmount] = useState('');
  const [editStagingDate, setEditStagingDate] = useState('');
  const [editStagingNotes, setEditStagingNotes] = useState('');

  // Bulk select + sort per this tab
  const [selectedImport, setSelectedImport] = useState(new Set());
  const [sortImport, setSortImport] = useState({ key: 'transaction_date', dir: 'desc' });

  const toggleSelectImport = (id) => {
    setSelectedImport(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAllImport = (ids) => {
    setSelectedImport(prev => prev.size === ids.length ? new Set() : new Set(ids));
  };

  const handleDownloadTemplate = () => {
    fetch(`${API_URL}/api/admin/investor-transactions/import-template/download`, { headers })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'investor_transactions_template.csv';
        a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error('Download failed'));
  };

  const handleImport = async (dryRun = false) => {
    if (!importFile) { toast.error('Please select a file'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const qs = new URLSearchParams();
      if (dryRun) qs.set('dry_run', 'true');
      if (importForce) qs.set('force', 'true');
      const qsStr = qs.toString();
      const url = `${API_URL}/api/admin/investor-transactions/import-csv${qsStr ? `?${qsStr}` : ''}`;
      const r = await axios.post(url, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(r.data);
      if (dryRun) {
        toast.info(r.data.message || `DRY-RUN: would commit ${r.data.committed_count} rows`);
      } else {
        setImportFile(null);
        toast.success(`Committed ${r.data.committed_count ?? r.data.staged_count ?? 0} transactions`);
        if (r.data.skipped_duplicates > 0) toast.info(`${r.data.skipped_duplicates} duplicate row(s) silently skipped`);
        if (r.data.unmatched_investors > 0) toast.warning(`${r.data.unmatched_investors} row(s) had an unmatched investor code`);
        if (r.data.error_count > 0) toast.warning(`${r.data.error_count} row error(s) — see import summary`);
        fetchTransactions();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const fetchBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investor-transactions/pending-imports?show_completed=${showCompletedBatches}`, { headers });
      setBatches(r.data.batches || []);
    } catch (e) {
      toast.error('Failed to load import batches');
    } finally {
      setBatchesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showCompletedBatches]);

  const fetchBatchDetail = async (batchId) => {
    setBatchDetailLoading(true);
    setSelectedBatch(batchId);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investor-transactions/pending-imports/${batchId}`, { headers });
      setBatchDetail(r.data);
    } catch {
      toast.error('Failed to load batch detail');
    } finally {
      setBatchDetailLoading(false);
    }
  };

  const handleApproveBatch = async (batchId) => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/investor-transactions/pending-imports/${batchId}/approve`, {}, { headers });
      toast.success(r.data.message);
      fetchBatches();
      if (selectedBatch === batchId) fetchBatchDetail(batchId);
      fetchTransactions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
  };

  const handleRejectBatch = async (batchId) => {
    if (!window.confirm('Reject all pending records in this batch?')) return;
    try {
      await axios.post(`${API_URL}/api/admin/investor-transactions/pending-imports/${batchId}/reject`, {}, { headers });
      toast.success('Batch rejected');
      fetchBatches();
      if (selectedBatch === batchId) fetchBatchDetail(batchId);
    } catch { toast.error('Rejection failed'); }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Permanently delete ALL records in this batch? This cannot be undone.')) return;
    try {
      const r = await axios.delete(`${API_URL}/api/admin/investor-transactions/pending-imports/${batchId}`, { headers });
      toast.success(r.data.message);
      setSelectedBatch(null);
      setBatchDetail(null);
      fetchBatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleApproveItem = async (itemId) => {
    try {
      await axios.patch(`${API_URL}/api/admin/investor-transactions/staging/${itemId}/approve`, {}, { headers });
      toast.success('Approved');
      fetchBatchDetail(selectedBatch);
      fetchTransactions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRejectItem = async (itemId) => {
    try {
      await axios.patch(`${API_URL}/api/admin/investor-transactions/staging/${itemId}/reject`, {}, { headers });
      toast.success('Rejected');
      fetchBatchDetail(selectedBatch);
    } catch { toast.error('Failed'); }
  };

  const handleLinkInvestor = async () => {
    if (!linkInvestorId) return;
    try {
      await axios.patch(`${API_URL}/api/admin/investor-transactions/staging/${linkDialog.item.id}/link-investor?investor_id=${linkInvestorId}`, {}, { headers });
      toast.success('Investor linked');
      setLinkDialog({ open: false, item: null });
      setLinkInvestorId('');
      fetchBatchDetail(selectedBatch);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEditStaging = (rec) => {
    setEditingStaging(rec);
    setEditStagingType(rec.transaction_type);
    setEditStagingAmount(rec.amount?.toString() || '');
    setEditStagingDate(rec.transaction_date || '');
    setEditStagingNotes(rec.notes || '');
  };

  const handleSaveEditStaging = async () => {
    if (!editingStaging) return;
    try {
      const params = new URLSearchParams();
      if (editStagingType !== editingStaging.transaction_type) params.append('transaction_type', editStagingType);
      if (editStagingAmount !== editingStaging.amount?.toString()) params.append('amount', editStagingAmount);
      if (editStagingDate !== editingStaging.transaction_date) params.append('transaction_date', editStagingDate);
      if (editStagingNotes !== (editingStaging.notes || '')) params.append('notes', editStagingNotes);
      if (params.toString() === '') { setEditingStaging(null); return; }
      await axios.patch(`${API_URL}/api/admin/investor-transactions/staging/${editingStaging.id}/edit?${params}`, {}, { headers });
      toast.success('Record updated');
      setEditingStaging(null);
      fetchBatchDetail(selectedBatch);
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  return {
    // Upload
    importFile, setImportFile,
    importing,
    importResult, setImportResult,
    importForce, setImportForce,
    handleImport, handleDownloadTemplate,

    // Batches
    batches,
    batchesLoading,
    selectedBatch, setSelectedBatch,
    batchDetail,
    batchDetailLoading,
    showCompletedBatches, setShowCompletedBatches,
    fetchBatches, fetchBatchDetail,
    handleApproveBatch, handleRejectBatch, handleDeleteBatch,
    handleApproveItem, handleRejectItem,

    // Link-investor dialog
    linkDialog, setLinkDialog,
    linkInvestorId, setLinkInvestorId,
    handleLinkInvestor,

    // Edit-staging dialog
    editingStaging, setEditingStaging,
    editStagingType, setEditStagingType,
    editStagingAmount, setEditStagingAmount,
    editStagingDate, setEditStagingDate,
    editStagingNotes, setEditStagingNotes,
    handleEditStaging, handleSaveEditStaging,

    // Bulk + sort
    selectedImport, setSelectedImport,
    sortImport, setSortImport,
    toggleSelectImport, toggleSelectAllImport,
  };
}
